import { Box, FormControl, FormHelperText, FormLabel, Heading, Switch, Text, Textarea } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  EventContextDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { minRequestDays, minTenderDays } from "../../../../../common/procurement/karens";
import { visibleLength } from "../../../../../common/text/visibleLength";

type PublicContext = Extract<EventContextDto, { type: "Public" }>;

export const AcceleratedProcedureField = ({
  eventId,
  publicContext,
  isEditable = true,
}: {
  eventId: string;
  publicContext: PublicContext;
  isEditable?: boolean;
}) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const [justification, setJustification] = useState(publicContext.acceleratedJustification ?? "");
  const urgent = publicContext.acceleratedProcedure === true;
  const { regulation, foaPart, procedureType } = publicContext;
  const tenderDays = minTenderDays(regulation, foaPart, procedureType, true);
  const requestDays = minRequestDays(regulation, foaPart, procedureType, true);

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5" mb="8">
      <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap="6">
        <Box>
          <FormLabel htmlFor="accelerated-procedure" mb="1" cursor="pointer">
            <Heading as="h3" size="sm">
              {t("Urgent case")}
            </Heading>
          </FormLabel>
          <Text fontSize="sm" color="smMuted">
            {requestDays
              ? t(
                  "When it is impossible to keep the ordinary deadlines, the request deadline can be shortened to {{requestDays}} days and the tender deadline to {{tenderDays}} days.",
                  { requestDays, tenderDays },
                )
              : t(
                  "When it is impossible to keep the ordinary deadline, the tender deadline can be shortened to {{tenderDays}} days.",
                  { tenderDays },
                )}
          </Text>
        </Box>
        <Switch
          id="accelerated-procedure"
          isChecked={urgent}
          isDisabled={!isEditable}
          onChange={(event) => updateField({ publicContext: { acceleratedProcedure: event.target.checked } })}
          colorScheme="teal"
          mt="1"
        />
      </FormControl>
      {urgent && (
        <FormControl isRequired mt="5">
          <FormLabel fontSize="sm" fontWeight="semibold" mb="1">
            {t("Why the case is urgent")}
          </FormLabel>
          <Textarea
            rows={3}
            value={justification}
            isDisabled={!isEditable}
            onChange={(event) => {
              setJustification(event.target.value);
              updateField(
                {
                  publicContext: {
                    acceleratedJustification: visibleLength(event.target.value) > 0 ? event.target.value : null,
                  },
                },
                { debounceMs: 300 },
              );
            }}
          />
          <FormHelperText>{t("The justification is sent to Doffin with the notice.")}</FormHelperText>
        </FormControl>
      )}
    </Box>
  );
};
